import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { RecordingEvent, CameraInsights } from '../../types';
import { Play, Loader2, Film, Download, Sparkles, ShieldCheck, ArrowLeft, Clapperboard, Send } from 'lucide-react';

// Dedicated recordings screen (replaces the old modal): a camera's cloud
// event/snapshot timeline with on-demand hydration, Iris insights, and a "day
// reel" that stitches a day's clips into one timestamped video. Reached from
// Cloud Storage → a camera's "Recordings" button (/recordings?vuid=&name=).
export const RecordingsPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const vuid = params.get('vuid') || '';
  const cameraName = params.get('name') || vuid;

  const [dates, setDates] = useState<string[]>([]);
  const [date, setDate] = useState<string>('');
  const [events, setEvents] = useState<RecordingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RecordingEvent | null>(null);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insights, setInsights] = useState<CameraInsights | null>(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Day reel (server-stitched MP4 for the selected day).
  const [reelBuilding, setReelBuilding] = useState(false);
  const [reel, setReel] = useState<{ key: string; clips: number; date: string } | null>(null);
  const [reelNote, setReelNote] = useState<string | null>(null);

  const toggleInsights = async () => {
    const open = !insightsOpen;
    setInsightsOpen(open);
    if (open && !insights) {
      setInsightsLoading(true);
      try { setInsights(await api.getCameraInsights(vuid, 30)); } catch { /* ignore */ } finally { setInsightsLoading(false); }
    }
  };

  useEffect(() => {
    if (!vuid) { setLoading(false); return; }
    (async () => {
      try {
        const ds = await api.getRecordingDates(vuid);
        setDates(ds);
        setDate(ds[0] ?? '');
        if (ds.length === 0) setLoading(false);
      } catch {
        setError('Could not load this camera’s recordings.');
        setLoading(false);
      }
    })();
  }, [vuid]);

  const loadEvents = useCallback(async (d: string) => {
    setLoading(true);
    setSelected(null);
    setError(null);
    try {
      setEvents(await api.getCameraEvents(vuid, d));
    } catch {
      setError('Could not load events for this day.');
    } finally {
      setLoading(false);
    }
  }, [vuid]);

  // Changing the day clears any reel/note from the previous day.
  useEffect(() => { if (date) { setReel(null); setReelNote(null); loadEvents(date); } }, [date, loadEvents]);

  const thumb = (e: RecordingEvent): string | null =>
    e.posterKey ? api.mediaUrl(e.posterKey) : e.pictureUrl || null;

  const hydrate = async (e: RecordingEvent) => {
    setHydrating(true);
    setError(null);
    try {
      const ok = await api.hydrateRecording(vuid, e.id, e.occurredAt);
      if (ok) {
        const evs = await api.getCameraEvents(vuid, date);
        setEvents(evs);
        const fresh = evs.find((x) => x.id === e.id);
        setSelected(fresh ?? e);
      } else {
        setError('That clip could not be retrieved from the camera’s cloud for this moment.');
      }
    } catch (err) {
      setError((err as { message?: string })?.message || 'Could not retrieve the clip.');
    } finally {
      setHydrating(false);
    }
  };

  const buildReel = async () => {
    if (!date) return;
    setReelBuilding(true);
    setReel(null);
    setReelNote(null);
    setError(null);
    try {
      const r = await api.buildDayReel(vuid, date);
      setReel({ key: r.videoKey, clips: r.clips, date });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setReelNote('No clips could be retrieved for this day — the camera’s cloud may no longer hold this footage (past its retention window), or the day had only snapshots.');
      } else {
        setReelNote((err as { message?: string })?.message || 'Could not build the day reel right now.');
      }
    } finally {
      setReelBuilding(false);
    }
  };

  // Combinable = already-hydrated clips plus pointer clips the reel will hydrate
  // from eye4 before stitching. Only pure snapshots (no video, no file_id) can't
  // go into a reel.
  const combinableCount = events.filter((e) => e.videoKey || e.fileId).length;

  const fmtDateShort = (d: string) => {
    const p = d.split('-');
    return p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : d;
  };
  const fmtDateLong = (d: string) => {
    const p = d.split('-');
    return p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]).toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : d;
  };
  const fmtTime = (iso: string) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');

  return (
    <div>
      {/* Header */}
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/cloud-storage')} title="Back to Cloud Storage">
            <ArrowLeft size={14} /><span>Back</span>
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <Film size={18} color="var(--primary-light)" />
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Recordings — {cameraName}</h2>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontFamily: 'monospace' }}>{vuid}</p>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={toggleInsights} style={{ color: 'var(--primary-light)' }}>
          <Sparkles size={13} />
          <span>{insightsOpen ? 'Hide Iris insights' : 'Iris insights'}</span>
        </button>
      </div>

      {!vuid ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No camera selected.</div>
      ) : (
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
        {/* Iris insights */}
        {insightsOpen && (
          <div style={{ marginBottom: 14, border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-md)', padding: '12px 14px', background: 'rgba(255,255,255,0.02)' }}>
            {insightsLoading || !insights ? (
              <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={16} className="spin" /></div>
            ) : (
              <IrisPanel ins={insights} />
            )}
            <IrisAsk vuid={vuid} />
          </div>
        )}

        {/* Date selector + day reel action */}
        {dates.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, flex: 1, minWidth: 200 }}>
              {dates.slice(0, 60).map((d) => (
                <button
                  key={d}
                  onClick={() => setDate(d)}
                  style={{
                    flexShrink: 0, fontSize: 12, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${d === date ? 'var(--primary-light)' : 'var(--border-dim)'}`,
                    background: d === date ? 'rgba(56,189,248,0.12)' : 'transparent',
                    color: d === date ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: d === date ? 600 : 400,
                  }}
                >
                  {fmtDateShort(d)}
                </button>
              ))}
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={buildReel}
              disabled={reelBuilding || combinableCount === 0}
              title={combinableCount === 0 ? 'No clips on this day to combine' : `Combine ${combinableCount} clip(s) into one timestamped video`}
              style={{ flexShrink: 0 }}
            >
              {reelBuilding ? <Loader2 size={14} className="spin" /> : <Clapperboard size={14} />}
              <span>{reelBuilding ? 'Building day reel…' : 'Combine day into one video'}</span>
            </button>
          </div>
        )}

        {reelBuilding && (
          <div style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: 'var(--primary-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', marginBottom: 12, fontSize: 12.5 }}>
            Retrieving this day’s clips and stitching them into one video with timestamps — this can take a minute or two for a busy day. If it takes too long, run it again — clips are saved as they’re retrieved, so a second run finishes fast.
          </div>
        )}
        {reelNote && (
          <div style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', color: '#f59e0b', borderRadius: 'var(--radius-md)', padding: '8px 12px', marginBottom: 12, fontSize: 12.5 }}>{reelNote}</div>
        )}

        {/* Day reel player */}
        {reel && (
          <div style={{ marginBottom: 16, border: '1px solid rgba(56,189,248,0.35)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#000' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(56,189,248,0.10)' }}>
              <span style={{ fontSize: 12.5, color: 'var(--primary-light)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clapperboard size={14} /> Day reel — {fmtDateLong(reel.date)} · {reel.clips} clip{reel.clips === 1 ? '' : 's'} combined
              </span>
              <a href={api.mediaUrl(reel.key)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                <Download size={13} /><span>Download</span>
              </a>
            </div>
            <video src={api.mediaUrl(reel.key)} controls autoPlay style={{ width: '100%', maxHeight: 420, display: 'block', background: '#000' }} />
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-md)', padding: '8px 12px', marginBottom: 12, fontSize: 12.5 }}>{error}</div>
        )}

        {/* Player for the selected event */}
        {selected && (
          <div style={{ marginBottom: 14, background: '#000', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            {selected.videoKey ? (
              <video src={api.mediaUrl(selected.videoKey)} poster={thumb(selected) ?? undefined} controls autoPlay style={{ width: '100%', maxHeight: 360, display: 'block', background: '#000' }} />
            ) : (
              <div style={{ position: 'relative' }}>
                {thumb(selected) ? (
                  <img src={thumb(selected)!} alt="snapshot" style={{ width: '100%', maxHeight: 360, objectFit: 'contain', background: '#000', display: 'block' }} />
                ) : (
                  <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)' }}>No snapshot</div>
                )}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(0,0,0,0.35)' }}>
                  <span style={{ color: '#fff', fontSize: 12.5 }}>Snapshot only — the video clip hasn’t been pulled yet.</span>
                  <button className="btn btn-primary btn-sm" onClick={() => hydrate(selected)} disabled={hydrating}>
                    {hydrating ? <Loader2 size={14} className="spin" /> : <Film size={14} />}
                    <span>{hydrating ? 'Retrieving clip…' : 'Load video clip'}</span>
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.02)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtTime(selected.occurredAt)} · {selected.type || 'event'}</span>
              {selected.videoKey && (
                <a href={api.mediaUrl(selected.videoKey)} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                  <Download size={13} /><span>Download</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Event grid */}
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={20} className="spin" /></div>
        ) : dates.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No cloud recordings indexed for this camera.</div>
        ) : events.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No events on this day.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {events.map((e) => {
              const t = thumb(e);
              const sel = selected?.id === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  style={{
                    padding: 0, border: `2px solid ${sel ? 'var(--primary-light)' : 'var(--border-subtle)'}`,
                    borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer', background: '#000', textAlign: 'left',
                  }}
                >
                  <div style={{ position: 'relative', aspectRatio: '16/9', background: '#0b1220' }}>
                    {t ? (
                      <img src={t} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 11 }}>no image</div>
                    )}
                    <span style={{ position: 'absolute', bottom: 4, right: 4, background: e.videoKey ? 'rgba(16,185,129,0.9)' : 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {e.videoKey ? <><Play size={9} /> clip</> : 'snapshot'}
                    </span>
                  </div>
                  <div style={{ padding: '5px 7px', background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ fontSize: 11.5, color: 'var(--text-main)' }}>{fmtTime(e.occurredAt)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{e.type || 'event'}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

// Iris insights panel — the station's AI analytics for this one camera: how many
// real detections vs noise the AI filtered, the label breakdown, and the busiest
// hour over the last 30 days.
const IrisPanel: React.FC<{ ins: CameraInsights }> = ({ ins }) => {
  const total = ins.shown + ins.suppressed;
  const hours = ins.byHour.length === 24 ? ins.byHour : new Array(24).fill(0);
  const maxHour = Math.max(1, ...hours);
  const peak = hours.indexOf(Math.max(...hours));
  const hourLabel = (h: number) => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
  const labelColors: Record<string, string> = {
    person: 'var(--primary-light)', vehicle: '#a78bfa', sound: '#34d399', motion: 'var(--text-dim)',
  };
  const labels = Object.entries(ins.byLabel).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);

  const stat = (label: string, value: string, sub: string, color: string, icon: React.ReactNode) => (
    <div style={{ flex: 1, minWidth: 120, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-dim)' }}>{icon}<span>{label}</span></div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{sub}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {stat('Events analysed', total.toLocaleString(), `last ${ins.sinceDays} days`, 'var(--text-main)', <Sparkles size={13} />)}
        {stat('Real detections', ins.shown.toLocaleString(), `${100 - ins.noisePct}% of events`, 'var(--primary-light)', <Sparkles size={13} />)}
        {stat('Noise filtered', `${ins.noisePct}%`, `${ins.suppressed.toLocaleString()} junk events`, 'var(--success-text)', <ShieldCheck size={13} />)}
      </div>

      {labels.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {labels.map(([l, n]) => (
            <span key={l} style={{ fontSize: 11.5, color: labelColors[l] ?? 'var(--text-muted)', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)', borderRadius: 999, padding: '2px 9px' }}>
              {l} · {n.toLocaleString()}
            </span>
          ))}
        </div>
      )}

      {total > 0 && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>
            Activity by hour {peak >= 0 && hours[peak] > 0 && <>· peak around <span style={{ color: 'var(--primary-light)' }}>{hourLabel(peak)}</span></>}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 48 }}>
            {hours.map((n, h) => (
              <div key={h} title={`${hourLabel(h)} · ${n}`} style={{ flex: 1, height: `${Math.max(2, (n / maxHour) * 100)}%`, background: h === peak && n > 0 ? 'var(--primary-light)' : 'rgba(56,189,248,0.3)', borderRadius: 2 }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Ask Iris — the conversational half of the insights panel. Instead of only
// reading the stat tiles, an operator can ask a plain-English question about
// this one camera ("was anyone here after midnight last week?") and Claude
// answers over the camera's detection metadata, optionally with snapshot
// evidence. Backed by POST /v2/admin/alerts/ask.
type IrisTurn = { q: string; a: string; evidence: { label: string; at: string; picture: string }[] };
const SUGGESTED = ['Any people at night this week?', 'Busiest time of day?', 'Vehicles in the last 7 days?'];
const IrisAsk: React.FC<{ vuid: string }> = ({ vuid }) => {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState<IrisTurn[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    setBusy(true); setErr(null); setQ('');
    try {
      const res = await api.askIris(vuid, text);
      setTurns((t) => [...t, { q: text, a: res.answer || 'No answer.', evidence: res.evidence }]);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Iris could not answer right now.');
      setQ(text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14, borderTop: '1px dashed var(--border-dim)', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-dim)', marginBottom: 8 }}>
        <Sparkles size={12} /><span>Ask Iris about this camera</span>
      </div>

      {turns.map((t, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-main)', fontWeight: 600, marginBottom: 3 }}>{t.q}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{t.a}</div>
          {t.evidence.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
              {t.evidence.map((ev, j) => (
                <img key={j} src={ev.picture} alt={`${ev.label} ${ev.at}`} title={`${ev.label} · ${new Date(ev.at).toLocaleString()}`}
                  style={{ width: 68, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border-subtle)' }} />
              ))}
            </div>
          )}
        </div>
      ))}

      {turns.length === 0 && !busy && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {SUGGESTED.map((s) => (
            <button key={s} onClick={() => ask(s)} className="btn btn-secondary btn-sm"
              style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{s}</button>
          ))}
        </div>
      )}

      {err && <div style={{ fontSize: 12, color: 'var(--danger-text, #f87171)', marginBottom: 8 }}>{err}</div>}

      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} style={{ display: 'flex', gap: 8 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={busy}
          placeholder="e.g. When was the last person detected?"
          style={{ flex: 1, background: 'var(--bg-surface, rgba(255,255,255,0.03))', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '8px 11px', fontSize: 13, color: 'var(--text-main)' }}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !q.trim()}>
          {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
          <span>{busy ? 'Asking…' : 'Ask'}</span>
        </button>
      </form>
    </div>
  );
};
